import type {
  RenderBackend,
  RenderBackendStatus,
  RenderFrameView,
  RenderSurfaceView,
} from '../RenderBackend.js'
import {
  motionBlurTapAlpha,
  motionBlurTapCount,
  motionBlurTapDistance,
  writeMotionBlurVector,
} from '../motionBlur.js'
import {
  BUILT_IN_SHAPES,
  buildRenderPasses,
  resolveCustomSvgAspectFit,
  resolveFramePaint,
  type RenderPassKind,
  type ResolvedFramePaint,
  type ResolvedCustomShape,
} from '../shapes/shapeGeometry.js'

const INSTANCE_FLOATS = 7
const INSTANCE_STRIDE_BYTES = INSTANCE_FLOATS * Float32Array.BYTES_PER_ELEMENT
const MAX_FINITE_FLOAT32 = 3.4028234663852886e38
const MIN_INSTANCE_CAPACITY = 1_024
const MAX_INSTANCE_CAPACITY = 100_000
const LOW_WATERMARK_DELAY_MS = 5_000
// Built-in shapes render from analytic SDFs in the fragment shader, so uAtlas
// is only bound for custom SVG masks. A 1x1 transparent texture is the default
// sampler binding when no custom mask is active.
export const WEBGL_DEFAULT_SAMPLER_BYTES = 4
export const WEBGL_CUSTOM_MASK_SIZE = 128
export const WEBGL_CUSTOM_DISTANCE_ENCODING_SCALE = 2
export const WEBGL_CUSTOM_SAMPLE_BOUND = 0.5 - 0.5 / WEBGL_CUSTOM_MASK_SIZE
const CUSTOM_MASK_SIZE = WEBGL_CUSTOM_MASK_SIZE
const CUSTOM_DISTANCE_PADDING = 1
const CUSTOM_DISTANCE_SIZE = CUSTOM_MASK_SIZE + CUSTOM_DISTANCE_PADDING * 2

export const WEBGL_INSTANCE_LAYOUT = Object.freeze({
  strideBytes: INSTANCE_STRIDE_BYTES,
  floatsPerInstance: INSTANCE_FLOATS,
  baseQuadExtent: 1,
})
export const WEBGL_BASE_QUAD_VERTICES = Object.freeze([
  -1, -1, 1, -1, 1, 1, -1, 1,
] as const)

export type InstancedDrawPass = RenderPassKind

export interface InstancedFrameStyle {
  readonly paint: ResolvedFramePaint
  readonly surfaceWidth: number
  readonly surfaceHeight: number
  readonly effectiveDpr: number
  readonly viewportX: number
  readonly viewportY: number
  readonly viewportZoom: number
}

export interface PixiInstancedAdapter {
  readonly atlasByteLength: number
  resize(width: number, height: number, effectiveDpr: number): void
  replaceCapacity(capacity: number, data: Float32Array): void
  loadCustomMask(custom: ResolvedCustomShape): Promise<PixiCustomMaskResource>
  useCustomMask(resource: PixiCustomMaskResource | null): void
  upload(data: Float32Array, activeByteLength: number, visibleCount: number): void
  configure(style: InstancedFrameStyle): void
  draw(pass: InstancedDrawPass, motionSample?: number, motionAlpha?: number): void
  rebuild(
    activeCustomResource: PixiCustomMaskResource | null,
    staging: Float32Array,
    capacity: number,
  ): Promise<void>
  destroy(): void
}

export interface PixiCustomMaskResource {
  readonly key: string
  destroy(): void
}

export interface TimerSource {
  set(callback: () => void, delayMs: number): number
  clear(handle: number): void
}

const DEFAULT_TIMERS: TimerSource = {
  set: (callback, delayMs) => globalThis.setTimeout(callback, delayMs) as unknown as number,
  clear: (handle) => globalThis.clearTimeout(handle),
}

function nextPowerOfTwo(value: number): number {
  let result = 1
  while (result < value && result < MAX_INSTANCE_CAPACITY) result *= 2
  return Math.min(result, MAX_INSTANCE_CAPACITY)
}

function shapeIndex(shapeId: string): number {
  const index = (BUILT_IN_SHAPES as readonly string[]).indexOf(shapeId)
  return index < 0 ? 2 : index
}

function resolvedShapeExtent(paint: ResolvedFramePaint): number {
  if (paint.shape.kind === 'custom-svg') return 0.5
  const bounds = paint.shape.builtIn.bounds
  return Math.max(
    Math.abs(bounds[0]),
    Math.abs(bounds[1]),
    Math.abs(bounds[2]),
    Math.abs(bounds[3]),
  )
}

export function resolveWebGlQuadExtent(
  paint: ResolvedFramePaint,
  displaySize: number,
  viewportZoom: number,
): number {
  const shapeExtent = resolvedShapeExtent(paint)
  const screenSize = Math.max(0.0001, Math.abs(displaySize * viewportZoom))
  const antialiasWidth = 1 / screenSize
  let extent = shapeExtent + antialiasWidth
  if (paint.showBorder && paint.borderIntensity > 0) {
    const scale = Math.max(0, paint.borderScale)
    const borderWidth = Math.max(1 / screenSize, paint.borderWidth) * scale
    extent = Math.max(extent, shapeExtent * scale + borderWidth * 0.5 + antialiasWidth)
  }
  if (paint.showGlow && paint.glowIntensity > 0) {
    const glowWidth = Math.max(antialiasWidth, paint.glowSoftness / screenSize)
    extent = Math.max(extent, shapeExtent + glowWidth)
  }
  return extent
}

export class PixiParticleBackend implements RenderBackend {
  readonly kind = 'webgl2' as const
  private status: RenderBackendStatus = 'ready'
  private readonly statusListeners = new Set<(status: RenderBackendStatus) => void>()
  private readonly invalidationListeners = new Set<() => void>()
  private capacity = MIN_INSTANCE_CAPACITY
  private staging = new Float32Array(MIN_INSTANCE_CAPACITY * INSTANCE_FLOATS)
  private visibleCount = 0
  private budget = MAX_INSTANCE_CAPACITY
  private shrinkHandle: number | null = null
  private lifecycleRevision = 0
  private customRevision = 0
  private activeCustomKey = ''
  private pendingCustomKey = ''
  private readonly customCache = new Map<string, PixiCustomMaskResource>()
  private readonly motionVector = { x: 0, y: 0 }
  private readonly onContextLost = (event: Event): void => { this.handleContextLost(event) }
  private readonly onContextRestored = (): void => { this.handleContextRestored() }

  constructor(
    private readonly canvas: Pick<HTMLCanvasElement, 'addEventListener' | 'removeEventListener'>,
    private readonly adapter: PixiInstancedAdapter,
    private readonly timers: TimerSource = DEFAULT_TIMERS,
  ) {
    if (adapter.atlasByteLength <= 0 || adapter.atlasByteLength > 1_048_576) {
      throw new RangeError('Sampler texture byte length must be positive and bounded to one MiB')
    }
    this.adapter.replaceCapacity(this.capacity, this.staging)
    canvas.addEventListener('webglcontextlost', this.onContextLost)
    canvas.addEventListener('webglcontextrestored', this.onContextRestored)
  }

  getStatus(): RenderBackendStatus { return this.status }

  subscribeStatus(listener: (status: RenderBackendStatus) => void): () => void {
    if (typeof listener !== 'function') throw new TypeError('Status listener must be a function')
    this.statusListeners.add(listener)
    let active = true
    return () => {
      if (!active) return
      active = false
      this.statusListeners.delete(listener)
    }
  }

  subscribeInvalidation(listener: () => void): () => void {
    if (typeof listener !== 'function') throw new TypeError('Invalidation listener must be a function')
    this.invalidationListeners.add(listener)
    let active = true
    return () => {
      if (!active) return
      active = false
      this.invalidationListeners.delete(listener)
    }
  }

  resize(surface: Readonly<RenderSurfaceView>): void {
    if (this.status !== 'ready') return
    this.adapter.resize(surface.width, surface.height, surface.effectiveDpr)
  }

  render(frame: RenderFrameView): void {
    if (this.status !== 'ready') return
    const required = Math.min(frame.particles.renderCount, MAX_INSTANCE_CAPACITY)
    this.budget = Math.min(Math.max(MIN_INSTANCE_CAPACITY, frame.quality.particleBudget), MAX_INSTANCE_CAPACITY)
    if (required > this.capacity) this.grow(required)

    const particles = frame.particles
    const paint = resolveFramePaint(frame.project, frame.playback)
    const blurTaps = motionBlurTapCount(frame)
    this.adapter.useCustomMask(this.resolveCustomMask(paint))
    let output = 0
    for (let visibleIndex = 0; visibleIndex < required; visibleIndex += 1) {
      const index = particles.indices[visibleIndex]
      if (index === undefined || index >= particles.sourceCount) continue
      const x = particles.positionX[index]
      const y = particles.positionY[index]
      const size = particles.size[index]
      const rotation = particles.rotation[index]
      const alpha = particles.alpha[index]
      if (x === undefined || y === undefined || size === undefined || rotation === undefined || alpha === undefined) continue
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(size)
        || !Number.isFinite(rotation) || !Number.isFinite(alpha)) continue
      const displaySize = Math.max(0, size) * frame.project.particles.scale
      const finalAlpha = Math.max(0, Math.min(1, alpha * paint.fadeAlpha))
      if (!Number.isFinite(displaySize) || !Number.isFinite(finalAlpha)
        || Math.abs(x) > MAX_FINITE_FLOAT32 || Math.abs(y) > MAX_FINITE_FLOAT32
        || Math.abs(displaySize) > MAX_FINITE_FLOAT32 || Math.abs(rotation) > MAX_FINITE_FLOAT32
        || displaySize <= 0 || finalAlpha <= 0) continue
      const offset = output * INSTANCE_FLOATS
      this.staging[offset] = x
      this.staging[offset + 1] = y
      this.staging[offset + 2] = displaySize
      this.staging[offset + 3] = rotation
      this.staging[offset + 4] = finalAlpha
      if (blurTaps > 0 && writeMotionBlurVector(
        this.motionVector,
        x,
        y,
        particles.previousPositionX?.[index],
        particles.previousPositionY?.[index],
        frame.viewport.zoom,
      )) {
        this.staging[offset + 5] = this.motionVector.x
        this.staging[offset + 6] = this.motionVector.y
      } else {
        this.staging[offset + 5] = 0
        this.staging[offset + 6] = 0
      }
      output += 1
    }
    this.visibleCount = output
    this.adapter.upload(this.staging, output * INSTANCE_STRIDE_BYTES, output)
    this.adapter.configure({
      paint,
      surfaceWidth: frame.surface.width,
      surfaceHeight: frame.surface.height,
      effectiveDpr: frame.surface.effectiveDpr,
      viewportX: frame.viewport.x,
      viewportY: frame.viewport.y,
      viewportZoom: frame.viewport.zoom,
    })
    const passes = buildRenderPasses(paint)
    for (let tap = blurTaps; tap >= 1; tap -= 1) {
      const distance = motionBlurTapDistance(tap, blurTaps, frame.project.animation.motionBlur)
      const alpha = motionBlurTapAlpha(tap, blurTaps, frame.project.animation.motionBlur)
      for (let passIndex = 0; passIndex < passes.length; passIndex += 1) {
        const pass = passes[passIndex]
        if (pass !== undefined) this.adapter.draw(pass.kind, distance, alpha)
      }
    }
    for (let passIndex = 0; passIndex < passes.length; passIndex += 1) {
      const pass = passes[passIndex]
      if (pass !== undefined) this.adapter.draw(pass.kind, 0, 1)
    }
    this.updateShrinkTimer(output)
  }

  dispose(): void {
    if (this.status === 'disposed') return
    this.lifecycleRevision += 1
    this.customRevision += 1
    this.clearShrinkTimer()
    this.canvas.removeEventListener('webglcontextlost', this.onContextLost)
    this.canvas.removeEventListener('webglcontextrestored', this.onContextRestored)
    this.status = 'disposed'
    for (const resource of this.customCache.values()) resource.destroy()
    this.customCache.clear()
    this.adapter.destroy()
    this.statusListeners.clear()
    this.invalidationListeners.clear()
    this.staging = new Float32Array(0)
    this.capacity = 0
    this.visibleCount = 0
  }

  getDebugSnapshot(): Readonly<{
    capacity: number
    visibleCount: number
    instanceStrideBytes: number
    stagingByteLength: number
    atlasByteLength: number
    particleObjectAllocations: 0
    shrinkTimerArmed: boolean
  }> {
    return {
      capacity: this.capacity,
      visibleCount: this.visibleCount,
      instanceStrideBytes: INSTANCE_STRIDE_BYTES,
      stagingByteLength: this.staging.byteLength,
      atlasByteLength: this.adapter.atlasByteLength,
      particleObjectAllocations: 0,
      shrinkTimerArmed: this.shrinkHandle !== null,
    }
  }

  private setStatus(status: RenderBackendStatus): void {
    if (status === this.status) return
    this.status = status
    for (const listener of this.statusListeners) {
      try { listener(status) } catch { /* Status observers cannot break backend lifecycle. */ }
    }
  }

  private emitInvalidation(): void {
    for (const listener of this.invalidationListeners) {
      try { listener() } catch { /* Resource observers cannot break backend lifecycle. */ }
    }
  }

  private resolveCustomMask(paint: ResolvedFramePaint): PixiCustomMaskResource | null {
    const custom = paint.shape.custom
    if (paint.shape.kind !== 'custom-svg' || custom === undefined) {
      if (this.activeCustomKey !== '') {
        this.activeCustomKey = ''
        this.pendingCustomKey = ''
        this.customRevision += 1
      }
      return null
    }
    if (this.activeCustomKey !== custom.stableKey) {
      this.activeCustomKey = custom.stableKey
      this.pendingCustomKey = ''
      this.customRevision += 1
    }
    const cached = this.customCache.get(custom.stableKey)
    if (cached !== undefined) {
      this.customCache.delete(custom.stableKey)
      this.customCache.set(custom.stableKey, cached)
      return cached
    }
    if (this.pendingCustomKey === custom.stableKey) return null
    this.pendingCustomKey = custom.stableKey
    const revision = this.customRevision
    void this.adapter.loadCustomMask(custom).then((resource) => {
      if (
        this.status !== 'ready'
        || revision !== this.customRevision
        || this.activeCustomKey !== custom.stableKey
      ) {
        resource.destroy()
        return
      }
      this.pendingCustomKey = ''
      this.customCache.set(custom.stableKey, resource)
      while (this.customCache.size > 2) {
        const oldest = this.customCache.keys().next().value
        if (oldest === undefined) break
        const evicted = this.customCache.get(oldest)
        this.customCache.delete(oldest)
        evicted?.destroy()
      }
      this.emitInvalidation()
    }).catch(() => {
      if (revision === this.customRevision) this.pendingCustomKey = ''
    })
    return null
  }

  private grow(required: number): void {
    this.clearShrinkTimer()
    const newCapacity = Math.min(MAX_INSTANCE_CAPACITY, nextPowerOfTwo(required))
    if (newCapacity <= this.capacity) return
    this.capacity = newCapacity
    this.staging = new Float32Array(newCapacity * INSTANCE_FLOATS)
    this.adapter.replaceCapacity(newCapacity, this.staging)
  }

  private updateShrinkTimer(demand: number): void {
    if (this.capacity <= MIN_INSTANCE_CAPACITY || demand > this.capacity / 4) {
      this.clearShrinkTimer()
      return
    }
    if (this.shrinkHandle !== null) return
    const revision = this.lifecycleRevision
    this.shrinkHandle = this.timers.set(() => {
      this.shrinkHandle = null
      if (this.status !== 'ready' || revision !== this.lifecycleRevision) return
      const target = Math.min(
        this.budget,
        Math.max(MIN_INSTANCE_CAPACITY, nextPowerOfTwo(Math.max(1, this.visibleCount) * 2)),
      )
      if (target >= this.capacity) return
      this.capacity = target
      this.staging = new Float32Array(target * INSTANCE_FLOATS)
      this.adapter.replaceCapacity(target, this.staging)
    }, LOW_WATERMARK_DELAY_MS)
  }

  private clearShrinkTimer(): void {
    if (this.shrinkHandle === null) return
    this.timers.clear(this.shrinkHandle)
    this.shrinkHandle = null
  }

  private handleContextLost(event: Event): void {
    if (this.status === 'disposed') return
    if ('preventDefault' in event && typeof event.preventDefault === 'function') event.preventDefault()
    this.lifecycleRevision += 1
    this.customRevision += 1
    this.pendingCustomKey = ''
    this.clearShrinkTimer()
    this.setStatus('context-lost')
  }

  private handleContextRestored(): void {
    if (this.status !== 'context-lost') return
    const revision = ++this.lifecycleRevision
    const activeCustomResource = this.activeCustomKey === ''
      ? null
      : this.customCache.get(this.activeCustomKey) ?? null
    this.setStatus('initializing')
    let rebuild: Promise<void>
    try {
      rebuild = this.adapter.rebuild(activeCustomResource, this.staging, this.capacity)
    } catch {
      if (revision === this.lifecycleRevision) this.setStatus('context-lost')
      return
    }
    void rebuild.then(
      () => {
        if (revision !== this.lifecycleRevision || this.status !== 'initializing') return
        this.setStatus('ready')
        this.emitInvalidation()
      },
      () => {
        if (revision !== this.lifecycleRevision || this.status !== 'initializing') return
        this.setStatus('context-lost')
      },
    )
  }
}

export function buildCustomDistancePixels(alpha: Uint8ClampedArray): Uint8Array {
  const sourcePixelCount = CUSTOM_MASK_SIZE * CUSTOM_MASK_SIZE
  if (alpha.length !== sourcePixelCount * 4) {
    throw new RangeError('Custom WebGL mask must be an exact 128×128 RGBA raster')
  }
  const distancePixelCount = CUSTOM_DISTANCE_SIZE * CUSTOM_DISTANCE_SIZE
  const coverage = new Uint8Array(distancePixelCount)
  const distanceToInside = new Float32Array(distancePixelCount)
  const distanceToOutside = new Float32Array(distancePixelCount)
  for (let y = 0; y < CUSTOM_MASK_SIZE; y += 1) {
    for (let x = 0; x < CUSTOM_MASK_SIZE; x += 1) {
      const sourceIndex = y * CUSTOM_MASK_SIZE + x
      const distanceIndex = (y + CUSTOM_DISTANCE_PADDING) * CUSTOM_DISTANCE_SIZE
        + x + CUSTOM_DISTANCE_PADDING
      coverage[distanceIndex] = (alpha[sourceIndex * 4 + 3] ?? 0) > 0 ? 1 : 0
    }
  }
  for (let index = 0; index < distancePixelCount; index += 1) {
    const inside = coverage[index] === 1
    distanceToInside[index] = inside ? 0 : Number.POSITIVE_INFINITY
    distanceToOutside[index] = inside ? Number.POSITIVE_INFINITY : 0
  }
  const transform = (distances: Float32Array): void => {
    for (let y = 0; y < CUSTOM_DISTANCE_SIZE; y += 1) {
      for (let x = 0; x < CUSTOM_DISTANCE_SIZE; x += 1) {
        const index = y * CUSTOM_DISTANCE_SIZE + x
        let distance = distances[index] ?? Number.POSITIVE_INFINITY
        if (x > 0) distance = Math.min(distance, (distances[index - 1] ?? distance) + 1)
        if (y > 0) distance = Math.min(distance, (distances[index - CUSTOM_DISTANCE_SIZE] ?? distance) + 1)
        if (x > 0 && y > 0) distance = Math.min(distance, (distances[index - CUSTOM_DISTANCE_SIZE - 1] ?? distance) + Math.SQRT2)
        if (x + 1 < CUSTOM_DISTANCE_SIZE && y > 0) distance = Math.min(distance, (distances[index - CUSTOM_DISTANCE_SIZE + 1] ?? distance) + Math.SQRT2)
        distances[index] = distance
      }
    }
    for (let y = CUSTOM_DISTANCE_SIZE - 1; y >= 0; y -= 1) {
      for (let x = CUSTOM_DISTANCE_SIZE - 1; x >= 0; x -= 1) {
        const index = y * CUSTOM_DISTANCE_SIZE + x
        let distance = distances[index] ?? Number.POSITIVE_INFINITY
        if (x + 1 < CUSTOM_DISTANCE_SIZE) distance = Math.min(distance, (distances[index + 1] ?? distance) + 1)
        if (y + 1 < CUSTOM_DISTANCE_SIZE) distance = Math.min(distance, (distances[index + CUSTOM_DISTANCE_SIZE] ?? distance) + 1)
        if (x + 1 < CUSTOM_DISTANCE_SIZE && y + 1 < CUSTOM_DISTANCE_SIZE) distance = Math.min(distance, (distances[index + CUSTOM_DISTANCE_SIZE + 1] ?? distance) + Math.SQRT2)
        if (x > 0 && y + 1 < CUSTOM_DISTANCE_SIZE) distance = Math.min(distance, (distances[index + CUSTOM_DISTANCE_SIZE - 1] ?? distance) + Math.SQRT2)
        distances[index] = distance
      }
    }
  }
  transform(distanceToInside)
  transform(distanceToOutside)
  const pixels = new Uint8Array(sourcePixelCount * 4)
  for (let y = 0; y < CUSTOM_MASK_SIZE; y += 1) {
    for (let x = 0; x < CUSTOM_MASK_SIZE; x += 1) {
      const sourceIndex = y * CUSTOM_MASK_SIZE + x
      const distanceIndex = (y + CUSTOM_DISTANCE_PADDING) * CUSTOM_DISTANCE_SIZE
        + x + CUSTOM_DISTANCE_PADDING
      const inside = coverage[distanceIndex] === 1
      const distance = inside ? distanceToOutside[distanceIndex] : distanceToInside[distanceIndex]
      const signed = Math.max(-128 / WEBGL_CUSTOM_DISTANCE_ENCODING_SCALE, Math.min(
        127 / WEBGL_CUSTOM_DISTANCE_ENCODING_SCALE,
        inside ? (distance ?? 16) - 0.5 : -((distance ?? 16) - 0.5),
      ))
      const offset = sourceIndex * 4
      pixels[offset] = 255
      pixels[offset + 1] = 255
      pixels[offset + 2] = 255
      pixels[offset + 3] = Math.max(0, Math.min(
        255,
        Math.round(128 + signed * WEBGL_CUSTOM_DISTANCE_ENCODING_SCALE),
      ))
    }
  }
  return pixels
}

/** Mirrors the custom-mask texture sampling and outside extrapolation in the fragment shader. */
export function probeCustomDistancePixels(pixels: Uint8Array, x: number, y: number): number {
  if (pixels.length !== CUSTOM_MASK_SIZE * CUSTOM_MASK_SIZE * 4) {
    throw new RangeError('Custom WebGL distance texture must be exactly 128×128 RGBA')
  }
  const sampledX = Math.max(-WEBGL_CUSTOM_SAMPLE_BOUND, Math.min(WEBGL_CUSTOM_SAMPLE_BOUND, x))
  const sampledY = Math.max(-WEBGL_CUSTOM_SAMPLE_BOUND, Math.min(WEBGL_CUSTOM_SAMPLE_BOUND, y))
  const textureX = (sampledX + 0.5) * CUSTOM_MASK_SIZE - 0.5
  const textureY = (sampledY + 0.5) * CUSTOM_MASK_SIZE - 0.5
  const x0 = Math.max(0, Math.min(CUSTOM_MASK_SIZE - 1, Math.floor(textureX)))
  const y0 = Math.max(0, Math.min(CUSTOM_MASK_SIZE - 1, Math.floor(textureY)))
  const x1 = Math.min(CUSTOM_MASK_SIZE - 1, x0 + 1)
  const y1 = Math.min(CUSTOM_MASK_SIZE - 1, y0 + 1)
  const fractionX = textureX - x0
  const fractionY = textureY - y0
  const alphaAt = (pixelX: number, pixelY: number): number => pixels[
    (pixelY * CUSTOM_MASK_SIZE + pixelX) * 4 + 3
  ] ?? 0
  const top = alphaAt(x0, y0) * (1 - fractionX) + alphaAt(x1, y0) * fractionX
  const bottom = alphaAt(x0, y1) * (1 - fractionX) + alphaAt(x1, y1) * fractionX
  const encoded = top * (1 - fractionY) + bottom * fractionY
  const sampledDistance = ((encoded - 128) / WEBGL_CUSTOM_DISTANCE_ENCODING_SCALE) / CUSTOM_MASK_SIZE
  return sampledDistance - Math.hypot(x - sampledX, y - sampledY)
}

export const WEBGL_VERTEX_SHADER_SOURCE = `precision mediump float;
in vec2 aVertex;
in vec2 aPosition;
in float aSize;
in float aRotation;
in float aAlpha;
in vec2 aMotion;
uniform vec2 uSurface;
uniform vec3 uViewport;
uniform float uMotionSample;
uniform float uMotionAlpha;
uniform float uShapeExtent;
uniform float uBorderScale;
uniform float uBorderWidth;
uniform float uGlowSoftness;
uniform float uBorderEnabled;
uniform float uGlowEnabled;
out vec2 vShapePosition;
out float vAlpha;
out float vScreenSize;
void main(void) {
  float c = cos(aRotation);
  float s = sin(aRotation);
  float screenSize = max(0.0001, abs(aSize * uViewport.z));
  float antialiasWidth = 1.0 / screenSize;
  float borderScale = max(0.0, uBorderScale);
  float borderWidth = max(1.0 / screenSize, uBorderWidth) * borderScale;
  float fillExtent = uShapeExtent + antialiasWidth;
  float borderExtent = uShapeExtent * borderScale + borderWidth * 0.5 + antialiasWidth;
  float glowExtent = uShapeExtent + max(antialiasWidth, uGlowSoftness / screenSize);
  float extent = fillExtent;
  if (uBorderEnabled > 0.5) extent = max(extent, borderExtent);
  if (uGlowEnabled > 0.5) extent = max(extent, glowExtent);
  vec2 shapePosition = aVertex * extent;
  vec2 local = shapePosition * aSize;
  vec2 rotated = vec2(local.x * c - local.y * s, local.x * s + local.y * c);
  vec2 center = uSurface * 0.5;
  vec2 sampledPosition = aPosition + aMotion * uMotionSample;
  vec2 screen = center + (sampledPosition - center + uViewport.xy + rotated) * uViewport.z;
  vec2 clip = vec2(screen.x / uSurface.x * 2.0 - 1.0, 1.0 - screen.y / uSurface.y * 2.0);
  gl_Position = vec4(clip, 0.0, 1.0);
  vShapePosition = shapePosition;
  float moving = step(0.5, length(aMotion * uViewport.z));
  float ghost = step(0.0001, uMotionSample);
  vAlpha = aAlpha * uMotionAlpha * mix(1.0, moving, ghost);
  vScreenSize = max(0.0001, abs(aSize * uViewport.z));
}`

// Built-in shapes are exact analytic signed-distance fields evaluated per
// fragment (positive inside), so every shape stays pixel-sharp at any particle
// size or zoom with no texture atlas. uAtlas is sampled only for custom SVG
// masks, which are genuinely arbitrary rasters. The HEART polygon is the one
// non-closed-form shape: its two fixed cubic-bezier halves are pre-tessellated
// into 32 vertices (uHeart, with vertex 0 repeated at index 32 to close the
// loop), matched against the Canvas2D path to 0 hard mismatches. Everything
// stays GLSL ES 1.00-compatible (Pixi compiles these gl programs as ES 1.00
// even on WebGL2): no array constructors, and arrays are only indexed by loop
// symbols.
export const WEBGL_HEART_VERTEX_COUNT = 32
export const WEBGL_FRAGMENT_SHADER_SOURCE = `precision mediump float;
in vec2 vShapePosition;
in float vAlpha;
in float vScreenSize;
uniform sampler2D uAtlas;
uniform vec4 uFill;
uniform vec4 uBorder;
uniform vec4 uGlow;
uniform vec2 uHeart[33];
uniform float uPass;
uniform float uBorderScale;
uniform float uBorderWidth;
uniform float uGlowSoftness;
uniform float uSquareRadius;
uniform float uShapeIndex;
uniform float uCustomMask;
out vec4 finalColor;
float sdSeg(vec2 p, vec2 a, vec2 b) {
  vec2 pa = p - a, ba = b - a;
  float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
  return length(pa - ba * h);
}
float sdTriangleNeg(vec2 p, vec2 p0, vec2 p1, vec2 p2) {
  vec2 e0 = p1 - p0, e1 = p2 - p1, e2 = p0 - p2;
  vec2 v0 = p - p0, v1 = p - p1, v2 = p - p2;
  vec2 pq0 = v0 - e0 * clamp(dot(v0, e0) / dot(e0, e0), 0.0, 1.0);
  vec2 pq1 = v1 - e1 * clamp(dot(v1, e1) / dot(e1, e1), 0.0, 1.0);
  vec2 pq2 = v2 - e2 * clamp(dot(v2, e2) / dot(e2, e2), 0.0, 1.0);
  float s = sign(e0.x * e2.y - e0.y * e2.x);
  vec2 d = min(min(vec2(dot(pq0, pq0), s * (v0.x * e0.y - v0.y * e0.x)),
                   vec2(dot(pq1, pq1), s * (v1.x * e1.y - v1.y * e1.x))),
                   vec2(dot(pq2, pq2), s * (v2.x * e2.y - v2.y * e2.x)));
  return -sqrt(d.x) * sign(d.y);
}
float sdHexagonNeg(vec2 p, float r) {
  const vec3 k = vec3(-0.866025404, 0.5, 0.577350269);
  p = abs(p);
  p -= 2.0 * min(dot(k.xy, p), 0.0) * k.xy;
  p -= vec2(clamp(p.x, -k.z * r, k.z * r), r);
  return length(p) * sign(p.y);
}
float sdStar5Neg(vec2 p, float r, float rf) {
  const vec2 k1 = vec2(0.809016994375, -0.587785252292);
  const vec2 k2 = vec2(-k1.x, k1.y);
  p.x = abs(p.x);
  p -= 2.0 * max(dot(k1, p), 0.0) * k1;
  p -= 2.0 * max(dot(k2, p), 0.0) * k2;
  p.x = abs(p.x);
  p.y -= r;
  vec2 ba = rf * vec2(-k1.y, k1.x) - vec2(0.0, 1.0);
  float h = clamp(dot(p, ba) / dot(ba, ba), 0.0, r);
  return length(p - ba * h) * sign(p.y * ba.x - p.x * ba.y);
}
float sdHeartPos(vec2 p) {
  float d = dot(p - uHeart[0], p - uHeart[0]);
  float s = 1.0;
  for (int i = 0; i < 32; i++) {
    vec2 cur = uHeart[i];
    vec2 nxt = uHeart[i + 1];
    vec2 e = cur - nxt;
    vec2 w = p - nxt;
    vec2 b = w - e * clamp(dot(w, e) / dot(e, e), 0.0, 1.0);
    d = min(d, dot(b, b));
    bvec3 c = bvec3(p.y >= nxt.y, p.y < cur.y, e.x * w.y > e.y * w.x);
    if (all(c) || all(not(c))) s = -s;
  }
  return -s * sqrt(d);
}
float builtInDistance(vec2 p) {
  int idx = int(uShapeIndex + 0.5);
  if (idx == 0) {
    return 0.125 - min(sdSeg(p, vec2(-0.5, -0.5), vec2(0.5, 0.5)),
                       sdSeg(p, vec2(-0.5, 0.5), vec2(0.5, -0.5)));
  } else if (idx == 1) {
    float radius = clamp(uSquareRadius, 0.0, 0.5);
    vec2 q = abs(p) - vec2(0.5 - radius);
    return -(length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - radius);
  } else if (idx == 2) {
    return 0.5 - length(p);
  } else if (idx == 3) {
    return (0.7 - abs(p.x) - abs(p.y)) * 0.70710678;
  } else if (idx == 4) {
    return -sdStar5Neg(vec2(p.x, -p.y), 0.6, 0.41666667);
  } else if (idx == 5) {
    return 0.2 - length(p);
  } else if (idx == 6) {
    return -sdTriangleNeg(p, vec2(0.0, -0.6), vec2(0.6, 0.42), vec2(-0.6, 0.42));
  } else if (idx == 7) {
    return -sdHexagonNeg(vec2(p.y, p.x), 0.4330127);
  } else if (idx == 8) {
    return 0.15 - min(sdSeg(p, vec2(0.0, -0.5), vec2(0.0, 0.5)),
                      sdSeg(p, vec2(-0.5, 0.0), vec2(0.5, 0.0)));
  } else if (idx == 9) {
    return sdHeartPos(p);
  } else if (idx == 10) {
    return min(0.5 - length(p), length(p - vec2(0.25, 0.0)) - 0.375);
  } else if (idx == 11) {
    return 0.1 - abs(length(p) - 0.4);
  }
  return 0.5 - length(p);
}
float shapeDistance(vec2 position) {
  if (uCustomMask > 0.5) {
    vec2 bounds = vec2(0.5 - 0.5 / 128.0);
    vec2 sampledPosition = clamp(position, -bounds, bounds);
    float outsideDistance = length(position - sampledPosition);
    vec2 uv = sampledPosition + 0.5;
    float encoded = texture(uAtlas, uv).a * 255.0;
    float sampledDistance = ((encoded - 128.0) / 2.0) * (1.0 / 128.0);
    return sampledDistance - outsideDistance;
  }
  return builtInDistance(position);
}
void main(void) {
  float antialiasWidth = 1.0 / vScreenSize;
  float fillDistance = shapeDistance(vShapePosition);
  float fillCoverage = smoothstep(-antialiasWidth, antialiasWidth, fillDistance);
  float safeBorderScale = max(0.0001, uBorderScale);
  float borderDistance = shapeDistance(vShapePosition / safeBorderScale) * safeBorderScale;
  float borderWidth = max(1.0 / vScreenSize, uBorderWidth) * safeBorderScale;
  float borderCoverage = 1.0 - smoothstep(
    borderWidth * 0.5 - antialiasWidth,
    borderWidth * 0.5 + antialiasWidth,
    abs(borderDistance)
  );
  float glowWidth = max(antialiasWidth, uGlowSoftness / vScreenSize);
  float glowCoverage = smoothstep(-glowWidth, antialiasWidth, fillDistance);
  float fillAlpha = fillCoverage * uFill.a * vAlpha;
  float borderAlpha = borderCoverage * uBorder.a * vAlpha;
  float glowAlpha = glowCoverage * uGlow.a * vAlpha;
  vec4 fill = vec4(uFill.rgb * fillAlpha, fillAlpha);
  vec4 border = vec4(uBorder.rgb * borderAlpha, borderAlpha);
  vec4 glow = vec4(uGlow.rgb * glowAlpha, glowAlpha);
  if (uPass > 1.5) finalColor = border;
  else if (uPass > 0.5) finalColor = fill + glow * (1.0 - fill.a);
  else finalColor = border + fill * (1.0 - border.a) + glow * (1.0 - fill.a) * (1.0 - border.a);
}`

// The fixed heart outline: its two cubic-bezier halves tessellated into 32
// points, with vertex 0 repeated at index 32 so the shader can close the loop
// using only loop-symbol indices (uHeart[i], uHeart[i + 1]).
const WEBGL_HEART_VERTICES: readonly number[] = [
  0.0, 0.225, -0.07663, 0.15817, -0.13843, 0.09325, -0.18638, 0.0311,
  -0.22148, -0.02742, -0.24472, -0.08146, -0.25708, -0.13017, -0.25955, -0.17267,
  -0.25313, -0.20813, -0.23879, -0.23567, -0.21753, -0.25444, -0.19034, -0.26359,
  -0.1582, -0.26227, -0.12211, -0.2496, -0.08306, -0.22474, -0.04202, -0.18682,
  0.0, -0.135, 0.04202, -0.18682, 0.08306, -0.22474, 0.12211, -0.2496,
  0.1582, -0.26227, 0.19034, -0.26359, 0.21753, -0.25444, 0.23879, -0.23567,
  0.25313, -0.20813, 0.25955, -0.17267, 0.25708, -0.13017, 0.24472, -0.08146,
  0.22148, -0.02742, 0.18638, 0.0311, 0.13843, 0.09325, 0.07663, 0.15817,
]

export function buildHeartUniform(): Float32Array {
  const values = new Float32Array((WEBGL_HEART_VERTEX_COUNT + 1) * 2)
  values.set(WEBGL_HEART_VERTICES)
  values[WEBGL_HEART_VERTEX_COUNT * 2] = WEBGL_HEART_VERTICES[0] ?? 0
  values[WEBGL_HEART_VERTEX_COUNT * 2 + 1] = WEBGL_HEART_VERTICES[1] ?? 0
  return values
}

class PixiMeshAdapter implements PixiInstancedAdapter {
  readonly atlasByteLength: number
  private firstPass = true
  private customMaskActive = false
  private readonly clearColor = new Float32Array(4)

  constructor(
    private readonly renderer: import('pixi.js').WebGLRenderer,
    private readonly stage: import('pixi.js').Container,
    private readonly mesh: import('pixi.js').Mesh<import('pixi.js').Geometry, import('pixi.js').Shader>,
    private readonly geometry: import('pixi.js').Geometry,
    private instanceBuffer: import('pixi.js').Buffer,
    private readonly shader: import('pixi.js').Shader,
    private readonly atlas: import('pixi.js').Texture,
    atlasBytes: number,
  ) {
    this.atlasByteLength = atlasBytes
  }

  resize(width: number, height: number, effectiveDpr: number): void {
    this.renderer.resolution = effectiveDpr
    this.renderer.resize(width, height)
  }

  replaceCapacity(_capacity: number, data: Float32Array): void {
    this.instanceBuffer.data = data
    this.instanceBuffer.update(0)
  }

  async loadCustomMask(custom: ResolvedCustomShape): Promise<PixiCustomMaskResource> {
    if (typeof globalThis.fetch !== 'function' || typeof globalThis.createImageBitmap !== 'function') {
      throw new Error('Custom mask decoding is unavailable')
    }
    const response = await globalThis.fetch(custom.dataUrl)
    if (!response.ok) throw new Error(`Custom mask decode failed with ${String(response.status)}`)
    const bitmap = await globalThis.createImageBitmap(await response.blob(), {
      premultiplyAlpha: 'premultiply',
      colorSpaceConversion: 'none',
    })
    const surface = typeof globalThis.OffscreenCanvas === 'function'
      ? new OffscreenCanvas(CUSTOM_MASK_SIZE, CUSTOM_MASK_SIZE)
      : typeof globalThis.document !== 'undefined'
        ? document.createElement('canvas')
        : null
    if (surface === null) {
      bitmap.close()
      throw new Error('Custom WebGL mask raster canvas is unavailable')
    }
    surface.width = CUSTOM_MASK_SIZE
    surface.height = CUSTOM_MASK_SIZE
    const context = surface.getContext('2d', { willReadFrequently: true })
    if (context === null || !('getImageData' in context)) {
      bitmap.close()
      throw new Error('Custom WebGL mask raster context is unavailable')
    }
    const fit = resolveCustomSvgAspectFit(custom, CUSTOM_MASK_SIZE, CUSTOM_MASK_SIZE)
    const [destinationX, destinationY, width, height] = fit.destination
    context.clearRect(0, 0, CUSTOM_MASK_SIZE, CUSTOM_MASK_SIZE)
    context.drawImage(bitmap, destinationX, destinationY, width, height)
    bitmap.close()
    const pixels = buildCustomDistancePixels(
      context.getImageData(0, 0, CUSTOM_MASK_SIZE, CUSTOM_MASK_SIZE).data,
    )
    surface.width = 1
    surface.height = 1
    const pixi = await import('pixi.js')
    const source = new pixi.BufferImageSource({
      resource: pixels,
      width: CUSTOM_MASK_SIZE,
      height: CUSTOM_MASK_SIZE,
      scaleMode: 'linear',
      addressMode: 'clamp-to-edge',
      autoGenerateMipmaps: false,
    })
    const texture = new pixi.Texture({ source, label: `nastaliqart-v2-custom-${custom.stableKey.length}` })
    let destroyed = false
    return {
      key: custom.stableKey,
      texture,
      destroy: () => {
        if (destroyed) return
        destroyed = true
        texture.destroy(true)
      },
    } as PixiCustomMaskResource & { texture: import('pixi.js').Texture }
  }

  useCustomMask(resource: PixiCustomMaskResource | null): void {
    const custom = resource as (PixiCustomMaskResource & { texture?: import('pixi.js').Texture }) | null
    const source = custom?.texture?.source ?? this.atlas.source
    if (this.shader.resources.uAtlas !== source) this.shader.resources.uAtlas = source
    this.customMaskActive = custom?.texture !== undefined
  }

  upload(data: Float32Array, activeByteLength: number, visibleCount: number): void {
    if (this.instanceBuffer.data !== data) this.instanceBuffer.data = data
    this.instanceBuffer.update(activeByteLength)
    this.geometry.instanceCount = visibleCount
  }

  configure(style: InstancedFrameStyle): void {
    const uniforms = this.shader.resources.renderUniforms as {
      uniforms: Record<string, number | Float32Array>
      update(): void
    }
    const fill = uniforms.uniforms.uFill as Float32Array
    const border = uniforms.uniforms.uBorder as Float32Array
    const glow = uniforms.uniforms.uGlow as Float32Array
    const surface = uniforms.uniforms.uSurface as Float32Array
    const viewport = uniforms.uniforms.uViewport as Float32Array
    surface[0] = style.surfaceWidth
    surface[1] = style.surfaceHeight
    viewport[0] = style.viewportX
    viewport[1] = style.viewportY
    viewport[2] = style.viewportZoom
    const index = shapeIndex(style.paint.shape.builtIn.id)
    uniforms.uniforms.uBorderScale = style.paint.borderScale
    uniforms.uniforms.uBorderWidth = style.paint.borderWidth
    uniforms.uniforms.uGlowSoftness = style.paint.glowSoftness
    uniforms.uniforms.uShapeExtent = resolvedShapeExtent(style.paint)
    uniforms.uniforms.uBorderEnabled = style.paint.showBorder && style.paint.borderIntensity > 0 ? 1 : 0
    uniforms.uniforms.uGlowEnabled = style.paint.showGlow && style.paint.glowIntensity > 0 ? 1 : 0
    uniforms.uniforms.uSquareRadius = style.paint.shape.squareRadius
    uniforms.uniforms.uShapeIndex = index
    uniforms.uniforms.uCustomMask = this.customMaskActive ? 1 : 0
    fill[0] = style.paint.fillColor[0] / 255
    fill[1] = style.paint.fillColor[1] / 255
    fill[2] = style.paint.fillColor[2] / 255
    fill[3] = 1
    border[0] = style.paint.borderColor[0] / 255
    border[1] = style.paint.borderColor[1] / 255
    border[2] = style.paint.borderColor[2] / 255
    border[3] = style.paint.showBorder ? style.paint.borderIntensity : 0
    glow[0] = style.paint.glowColor[0] / 255
    glow[1] = style.paint.glowColor[1] / 255
    glow[2] = style.paint.glowColor[2] / 255
    glow[3] = style.paint.showGlow ? style.paint.glowIntensity : 0
    this.clearColor[0] = style.paint.backgroundColor[0] / 255
    this.clearColor[1] = style.paint.backgroundColor[1] / 255
    this.clearColor[2] = style.paint.backgroundColor[2] / 255
    this.clearColor[3] = style.paint.showBackground ? 1 : 0
    uniforms.update()
    this.firstPass = true
  }

  draw(pass: InstancedDrawPass, motionSample = 0, motionAlpha = 1): void {
    const uniforms = this.shader.resources.renderUniforms as { uniforms: Record<string, number>; update(): void }
    uniforms.uniforms.uPass = pass === 'local-composite' ? 0 : pass === 'glow-fill' ? 1 : 2
    uniforms.uniforms.uMotionSample = motionSample
    uniforms.uniforms.uMotionAlpha = motionAlpha
    uniforms.update()
    this.renderer.render({ container: this.stage, clear: this.firstPass, clearColor: this.clearColor })
    this.firstPass = false
  }

  async rebuild(
    activeCustomResource: PixiCustomMaskResource | null,
    staging: Float32Array,
    capacity: number,
  ): Promise<void> {
    if (this.renderer.context.webGLVersion !== 2 || this.renderer.context.isLost) {
      throw new Error('A live WebGL2 context is required for resource restoration')
    }
    if (capacity < MIN_INSTANCE_CAPACITY || staging.length !== capacity * INSTANCE_FLOATS) {
      throw new RangeError('Restored instance staging does not match capacity')
    }
    this.geometry.unload()
    this.instanceBuffer.unload()
    this.atlas.source.unload()
    const custom = activeCustomResource as
      | (PixiCustomMaskResource & { texture?: import('pixi.js').Texture })
      | null
    custom?.texture?.source.unload()
    this.instanceBuffer.data = staging
    this.instanceBuffer.update(0)
    this.atlas.source.update()
    custom?.texture?.source.update()
    this.useCustomMask(activeCustomResource)
    this.geometry.instanceCount = 0
    this.renderer.resetState()
    this.renderer.render({ container: this.stage, clear: true, clearColor: this.clearColor })
    await Promise.resolve()
    if (this.renderer.context.webGLVersion !== 2 || this.renderer.context.isLost) {
      throw new Error('WebGL2 restoration did not produce a renderable context')
    }
  }

  destroy(): void {
    this.stage.removeChild(this.mesh)
    this.mesh.destroy()
    this.shader.destroy(true)
    this.geometry.destroy(true)
    this.atlas.destroy(true)
    this.renderer.destroy({ removeView: false })
  }
}

async function createPixiMeshAdapter(
  canvas: HTMLCanvasElement,
  context: WebGL2RenderingContext,
): Promise<PixiInstancedAdapter> {
  const pixi = await import('pixi.js')
  const renderer = new pixi.WebGLRenderer<HTMLCanvasElement>()
  await renderer.init({
    canvas,
    context,
    preferWebGLVersion: 2,
    antialias: true,
    autoDensity: false,
    backgroundAlpha: 0,
    resolution: 1,
  })
  if (renderer.context.webGLVersion !== 2) {
    renderer.destroy({ removeView: false })
    throw new Error('Pixi did not initialize a WebGL2 renderer')
  }
  const staging = new Float32Array(MIN_INSTANCE_CAPACITY * INSTANCE_FLOATS)
  const instanceBuffer = new pixi.Buffer({
    data: staging,
    usage: pixi.BufferUsage.VERTEX | pixi.BufferUsage.COPY_DST,
    shrinkToFit: false,
    label: 'nastaliqart-v2-instances',
  })
  const geometry = new pixi.Geometry({
    attributes: {
      aVertex: { buffer: new Float32Array(WEBGL_BASE_QUAD_VERTICES), format: 'float32x2' },
      aPosition: { buffer: instanceBuffer, format: 'float32x2', stride: INSTANCE_STRIDE_BYTES, offset: 0, instance: true },
      aSize: { buffer: instanceBuffer, format: 'float32', stride: INSTANCE_STRIDE_BYTES, offset: 8, instance: true },
      aRotation: { buffer: instanceBuffer, format: 'float32', stride: INSTANCE_STRIDE_BYTES, offset: 12, instance: true },
      aAlpha: { buffer: instanceBuffer, format: 'float32', stride: INSTANCE_STRIDE_BYTES, offset: 16, instance: true },
      aMotion: { buffer: instanceBuffer, format: 'float32x2', stride: INSTANCE_STRIDE_BYTES, offset: 20, instance: true },
    },
    indexBuffer: new Uint16Array([0, 1, 2, 0, 2, 3]),
    instanceCount: 0,
  })
  // Built-in shapes are analytic in the shader; uAtlas only ever samples a
  // custom SVG mask. Bind a 1x1 transparent default so the sampler is always
  // valid when no custom mask is active.
  const defaultSamplerPixels = new Uint8Array(WEBGL_DEFAULT_SAMPLER_BYTES)
  const atlasSource = new pixi.BufferImageSource({
    resource: defaultSamplerPixels,
    width: 1,
    height: 1,
    scaleMode: 'linear',
    addressMode: 'clamp-to-edge',
    autoGenerateMipmaps: false,
  })
  const atlas = new pixi.Texture({ source: atlasSource, label: 'nastaliqart-v2-default-sampler' })
  const renderUniforms = new pixi.UniformGroup({
    uSurface: { value: new Float32Array([1, 1]), type: 'vec2<f32>' },
    uViewport: { value: new Float32Array([0, 0, 1]), type: 'vec3<f32>' },
    uFill: { value: new Float32Array([1, 1, 1, 1]), type: 'vec4<f32>' },
    uBorder: { value: new Float32Array([1, 1, 1, 0]), type: 'vec4<f32>' },
    uGlow: { value: new Float32Array([1, 1, 1, 0]), type: 'vec4<f32>' },
    uHeart: { value: buildHeartUniform(), type: 'vec2<f32>', size: WEBGL_HEART_VERTEX_COUNT + 1 },
    uPass: { value: 0, type: 'f32' },
    uMotionSample: { value: 0, type: 'f32' },
    uMotionAlpha: { value: 1, type: 'f32' },
    uBorderScale: { value: 1, type: 'f32' },
    uBorderWidth: { value: 0.08, type: 'f32' },
    uGlowSoftness: { value: 0, type: 'f32' },
    uShapeExtent: { value: 0.5, type: 'f32' },
    uBorderEnabled: { value: 0, type: 'f32' },
    uGlowEnabled: { value: 0, type: 'f32' },
    uSquareRadius: { value: 0, type: 'f32' },
    uShapeIndex: { value: 0, type: 'f32' },
    uCustomMask: { value: 0, type: 'f32' },
  }, { isStatic: true })
  const shader = pixi.Shader.from({
    gl: { vertex: WEBGL_VERTEX_SHADER_SOURCE, fragment: WEBGL_FRAGMENT_SHADER_SOURCE, name: 'nastaliqart-v2-particles' },
    resources: { renderUniforms, uAtlas: atlas.source },
  })
  const mesh = new pixi.Mesh({ geometry, shader, texture: atlas })
  const stage = new pixi.Container()
  stage.addChild(mesh)
  return new PixiMeshAdapter(renderer, stage, mesh, geometry, instanceBuffer, shader, atlas, WEBGL_DEFAULT_SAMPLER_BYTES)
}

export async function createPixiParticleBackend(
  canvas: HTMLCanvasElement,
  context: WebGL2RenderingContext,
): Promise<RenderBackend> {
  const adapter = await createPixiMeshAdapter(canvas, context)
  return new PixiParticleBackend(canvas, adapter)
}
